// @ts-check
/** @typedef {import('../types/api').Vendor} Vendor */
/** @typedef {import('../types/api').VendorOrder} VendorOrder */
/** @typedef {import('../types/api').VendorInvoice} VendorInvoice */
/** @typedef {import('../types/api').VendorPayment} VendorPayment */
/** @typedef {import('../types/api').QualityIssue} QualityIssue */
/** @typedef {import('../types/api').VendorAnalytics} VendorAnalytics */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const vendorService = {
  // Vendors

  /**
   * @param {Object} [filters]
   * @returns {Promise<Vendor[]>}
   */
  async getVendors(filters = {}) {
    const params = new URLSearchParams();
    if (filters.vendor_type) params.append('vendor_type', filters.vendor_type);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.include_deleted) params.append('include_deleted', 'true');

    const data = await api.get(`/api/admin/vendors?${params}`);
    assertArray(data, 'vendorService.getVendors');
    return data;
  },

  /**
   * @param {string} vendorId
   * @returns {Promise<Vendor>}
   */
  async getVendor(vendorId) {
    const data = await api.get(`/api/admin/vendors/${vendorId}`);
    assertShape(data, ['id'], 'vendorService.getVendor');
    return data;
  },

  /**
   * @param {Object} vendorData
   * @returns {Promise<Vendor>}
   */
  async createVendor(vendorData) {
    return api.post('/api/admin/vendors', vendorData);
  },

  /**
   * @param {string} vendorId
   * @param {Object} vendorData
   * @returns {Promise<Vendor>}
   */
  async updateVendor(vendorId, vendorData) {
    return api.put(`/api/admin/vendors/${vendorId}`, vendorData);
  },

  /**
   * @param {string} vendorId
   * @param {boolean} [hardDelete=false]
   * @returns {Promise<void>}
   */
  async deleteVendor(vendorId, hardDelete = false) {
    const params = hardDelete ? '?hard_delete=true' : '';
    return api.del(`/api/admin/vendors/${vendorId}${params}`);
  },

  /**
   * @param {string} vendorId
   * @returns {Promise<Vendor>}
   */
  async restoreVendor(vendorId) {
    return api.post(`/api/admin/vendors/${vendorId}/restore`);
  },

  /**
   * @param {string} vendorId
   * @param {string} [startDate]
   * @param {string} [endDate]
   * @returns {Promise<VendorAnalytics>}
   */
  async getVendorAnalytics(vendorId, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const data = await api.get(`/api/admin/vendors/${vendorId}/analytics?${params}`);
    assertShape(data, ['vendor_id'], 'vendorService.getVendorAnalytics');
    return data;
  },

  // Vendor Orders

  /**
   * @param {Object} [filters]
   * @returns {Promise<VendorOrder[]>}
   */
  async getVendorOrders(filters = {}) {
    const params = new URLSearchParams();
    if (filters.vendor_id) params.append('vendor_id', filters.vendor_id);
    if (filters.status) params.append('status', filters.status);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.include_deleted) params.append('include_deleted', 'true');

    const data = await api.get(`/api/admin/vendors/orders?${params}`);
    assertArray(data, 'vendorService.getVendorOrders');
    return data;
  },

  /**
   * @param {string} orderId
   * @returns {Promise<VendorOrder>}
   */
  async getVendorOrder(orderId) {
    const data = await api.get(`/api/admin/vendors/orders/${orderId}`);
    assertShape(data, ['id'], 'vendorService.getVendorOrder');
    return data;
  },

  /**
   * @param {Object} orderData
   * @returns {Promise<VendorOrder>}
   */
  async createVendorOrder(orderData) {
    return api.post('/api/admin/vendors/orders', orderData);
  },

  /**
   * @param {string} orderId
   * @param {Object} orderData
   * @returns {Promise<VendorOrder>}
   */
  async updateVendorOrder(orderId, orderData) {
    return api.put(`/api/admin/vendors/orders/${orderId}`, orderData);
  },

  /**
   * @param {string} orderId
   * @param {boolean} [hardDelete=false]
   * @returns {Promise<void>}
   */
  async deleteVendorOrder(orderId, hardDelete = false) {
    const params = hardDelete ? '?hard_delete=true' : '';
    return api.del(`/api/admin/vendors/orders/${orderId}${params}`);
  },

  // Vendor Invoices

  /**
   * @param {Object} [filters]
   * @returns {Promise<VendorInvoice[]>}
   */
  async getVendorInvoices(filters = {}) {
    const params = new URLSearchParams();
    if (filters.vendor_id) params.append('vendor_id', filters.vendor_id);
    if (filters.vendor_order_id) params.append('vendor_order_id', filters.vendor_order_id);
    if (filters.payment_status) params.append('payment_status', filters.payment_status);

    const data = await api.get(`/api/admin/vendors/invoices?${params}`);
    assertArray(data, 'vendorService.getVendorInvoices');
    return data;
  },

  /**
   * @param {Object} invoiceData
   * @returns {Promise<VendorInvoice>}
   */
  async createVendorInvoice(invoiceData) {
    return api.post('/api/admin/vendors/invoices', invoiceData);
  },

  /**
   * @param {string} invoiceId
   * @param {Object} invoiceData
   * @returns {Promise<VendorInvoice>}
   */
  async updateVendorInvoice(invoiceId, invoiceData) {
    return api.put(`/api/admin/vendors/invoices/${invoiceId}`, invoiceData);
  },

  // Vendor Payments

  /**
   * @param {Object} [filters]
   * @returns {Promise<VendorPayment[]>}
   */
  async getVendorPayments(filters = {}) {
    const params = new URLSearchParams();
    if (filters.vendor_id) params.append('vendor_id', filters.vendor_id);
    if (filters.vendor_order_id) params.append('vendor_order_id', filters.vendor_order_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);

    const data = await api.get(`/api/admin/vendors/payments?${params}`);
    assertArray(data, 'vendorService.getVendorPayments');
    return data;
  },

  /**
   * @param {Object} paymentData
   * @returns {Promise<VendorPayment>}
   */
  async createVendorPayment(paymentData) {
    return api.post('/api/admin/vendors/payments', paymentData);
  },

  // Quality Issues

  /**
   * @param {Object} [filters]
   * @returns {Promise<QualityIssue[]>}
   */
  async getQualityIssues(filters = {}) {
    const params = new URLSearchParams();
    if (filters.vendor_id) params.append('vendor_id', filters.vendor_id);
    if (filters.vendor_order_id) params.append('vendor_order_id', filters.vendor_order_id);
    if (filters.status) params.append('status', filters.status);

    const data = await api.get(`/api/admin/vendors/quality-issues?${params}`);
    assertArray(data, 'vendorService.getQualityIssues');
    return data;
  },

  /**
   * @param {Object} issueData
   * @returns {Promise<QualityIssue>}
   */
  async createQualityIssue(issueData) {
    return api.post('/api/admin/vendors/quality-issues', issueData);
  },

  /**
   * @param {string} issueId
   * @param {Object} issueData
   * @returns {Promise<QualityIssue>}
   */
  async updateQualityIssue(issueId, issueData) {
    return api.put(`/api/admin/vendors/quality-issues/${issueId}`, issueData);
  },
};

export default vendorService;
